/**
 * EmailCollectionForm - Interstitial form shown after game completion, before results
 * Collects student email for teacher records
 */

import { useState, FormEvent } from 'react';
import { IoHappy, IoHeartDislike } from 'react-icons/io5';

interface EmailCollectionFormProps {
  playerName: string;
  teamName: string;
  gameCode: string;
  missionSuccess: boolean;
  onSubmit: (email: string) => Promise<void>;
  onSkip: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailCollectionForm({
  playerName,
  teamName,
  gameCode,
  missionSuccess,
  onSubmit,
  onSkip,
}: EmailCollectionFormProps) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function handleEmailChange(value: string) {
    setEmail(value);
    setSubmitError(null);

    if (value.length === 0) {
      setEmailError(null);
    } else if (!EMAIL_REGEX.test(value.trim())) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      await onSubmit(trimmed);
    } catch {
      setSubmitError('Failed to save email. You can skip and view your results.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center max-w-md mx-auto">
      {/* Mission outcome icon */}
      <div className={`size-20 rounded-full bg-gradient-to-br ${
        missionSuccess
          ? 'from-[#367A25] to-[#2d6820]'
          : 'from-[#DD6832] to-[#c4562a]'
      } flex items-center justify-center mb-4 shadow-lg`}>
        <span className="text-4xl text-white">
          {missionSuccess ? <IoHappy /> : <IoHeartDislike />}
        </span>
      </div>

      <h2 className="text-2xl font-bold mb-1 text-[#12356B]">
        {missionSuccess ? 'Mission Complete!' : 'Mission Failed'}
      </h2>
      <p className="text-slate-600 mb-6">
        Before viewing your results, please enter your email.
      </p>

      {/* Context info */}
      <div className="bg-[#E2E5EC]/70 border border-[#CED4DE] rounded-lg p-4 w-full mb-6 text-left">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-600">Player</span>
            <p className="font-medium">{playerName}</p>
          </div>
          <div>
            <span className="text-slate-600">Team</span>
            <p className="font-medium">{teamName}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-600">Game Code</span>
            <p className="font-semibold tracking-[0.12em] text-[#12356B]">{gameCode}</p>
          </div>
        </div>
      </div>

      {/* Email form */}
      <form onSubmit={handleSubmit} className="w-full space-y-4">
        <div className="text-left">
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="your.email@example.com"
            className="w-full px-4 py-3 border border-[#CED4DE] rounded-lg bg-white text-black placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B]"
            disabled={submitting}
            autoFocus
          />
          {emailError && (
            <p className="text-sm text-red-500 mt-1">{emailError}</p>
          )}
          {submitError && (
            <p className="text-sm text-red-500 mt-1">{submitError}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="submit"
            disabled={submitting || !email.trim() || !!emailError}
            className="w-full px-6 py-3 bg-[#367A25] text-white rounded-lg font-semibold hover:bg-[#2d6820] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Saving...' : 'View My Results'}
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={submitting}
            className="w-full px-6 py-3 border border-[#12356B] text-[#12356B] bg-white rounded-lg font-semibold hover:bg-[#E2E5EC] transition-colors"
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  );
}
